'use client'

import { useState, useCallback, useRef, useEffect } from 'react'
import Image from 'next/image'
import type { CarouselSectionFragment, ImageFragment } from '@/lib/types/datocms/graphql/generated'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import { TextWithHighlight } from '../TextWithHighlight'
import { StructuredText } from 'react-datocms'
import IconButton from '../ui/IconButton'
import clsx from 'clsx'
import Carousel from '../Carousel'

export default function CarouselSection({
	title,
	description,
	carousel,
}: Readonly<CarouselSectionFragment>) {
	const [currentImage, setCurrentImage] = useState(2)
	const [isAnimating, setIsAnimating] = useState(false)
	const [isTransitioning, setIsTransitioning] = useState(true)
	const transitionTimeoutRef = useRef<NodeJS.Timeout>()

	// Create extended carousel with 2 clones at each end using slice
	const extendedCarousel = [
		...carousel.slice(-2), // Last two images
		...carousel, // Original images
		...carousel.slice(0, 2), // First two images
	] as ImageFragment[]

	const handleNextImage = useCallback(() => {
		if (isAnimating) return
		setIsAnimating(true)
		setIsTransitioning(true)
		setCurrentImage((prev) => prev + 1)
	}, [isAnimating])

	const handlePreviousImage = useCallback(() => {
		if (isAnimating) return
		setIsAnimating(true)
		setIsTransitioning(true)
		setCurrentImage((prev) => prev - 1)
	}, [isAnimating])

	// Handle slide transitions
	const handleTransitionEnd = useCallback(() => {
		setIsAnimating(false)

		// Check if we need to reset position for infinite carousel effect
		const isAtStart = currentImage <= 1
		const isAtEnd = currentImage >= carousel.length + 2

		if (isAtStart || isAtEnd) {
			setIsTransitioning(false)
			setCurrentImage(isAtStart ? carousel.length + 1 : 2)
			transitionTimeoutRef.current = setTimeout(() => setIsTransitioning(true), 50)
		}
	}, [currentImage, carousel.length])

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (transitionTimeoutRef.current) {
				clearTimeout(transitionTimeoutRef.current)
			}
		}
	}, [])

	const carouselPortClassName =
		'w-[70vw] @5xl:[60vw] @7xl:w-[70vw] 2xl:w-[50vw] aspect-[3/2] max-h-[640px] bg-white-frosted rounded-lg overflow-hidden shrink-0'

	return (
		<section className="carousel-section @container">
			<div className="py-16 @3xl:py-20 max-w-screen-2xl mx-auto">
				<div className="common-x-padding text-center">
					{title && !isStructuredTextEmpty(title.text) && (
						<div className="heading-size-3">
							<TextWithHighlight text={title} />
						</div>
					)}
					{description?.value && !isStructuredTextEmpty(description) && (
						<div className="text-lg text-blue-dark/70 mt-4 @5xl:mt-5">
							<StructuredText data={description} />
						</div>
					)}
				</div>

				{/* Large Carousel Container (Desktop) */}
				<div className="hidden @xl:flex justify-center items-center gap-2.5 @3xl:gap-5 @5xl:gap-10 mt-12">
					{/* Left Carousel */}
					<div className={carouselPortClassName}>
						<CarouselSlider
							carousel={extendedCarousel}
							currentImage={currentImage - 1}
							imageClassName="object-right"
							onTransitionEnd={handleTransitionEnd}
							isTransitioning={isTransitioning}
							onClick={handlePreviousImage}
						/>
					</div>

					<IconButton
						icon="Arrow"
						onClick={handlePreviousImage}
						aria-label="Previous image"
						className="rotate-180 disabled:opacity-100"
						disabled={isAnimating}
					/>

					{/* Center Carousel */}
					<div className={carouselPortClassName}>
						<CarouselSlider
							carousel={extendedCarousel}
							currentImage={currentImage}
							onTransitionEnd={handleTransitionEnd}
							isTransitioning={isTransitioning}
						/>
					</div>

					<IconButton
						icon="Arrow"
						onClick={handleNextImage}
						aria-label="Next image"
						className="disabled:opacity-100"
						disabled={isAnimating}
					/>

					{/* Right Carousel */}
					<div className={carouselPortClassName}>
						<CarouselSlider
							carousel={extendedCarousel}
							currentImage={currentImage + 1}
							imageClassName="object-left"
							onTransitionEnd={handleTransitionEnd}
							isTransitioning={isTransitioning}
							onClick={handleNextImage}
						/>
					</div>
				</div>

				{/* Mobile Carousel (this is the Carousel used in other places on the site) */}
				<Carousel className="mt-12 @xl:hidden px-3" clickAndScroll>
					{carousel.length
						? carousel.map((image: ImageFragment, idx: number) => {
								if (!image.url) {
									return null
								}
								return (
									<div
										className="carousel-card max-h-[460px] min-w-[342px] aspect-[3/2] rounded-lg overflow-hidden"
										key={image.id}
									>
										<Image
											key={`${image.id}-${idx}`}
											src={image.url}
											alt={image.title ?? 'Carousel image'}
											width={100}
											height={100}
											sizes="80vw"
											className={clsx('object-cover h-full w-full')}
										/>
									</div>
								)
							})
						: null}
				</Carousel>
			</div>
		</section>
	)
}

// Large Carousel Slider
function CarouselSlider({
	carousel,
	currentImage,
	imageClassName,
	onTransitionEnd,
	isTransitioning,
	onClick,
}: {
	carousel: ImageFragment[]
	currentImage: number
	imageClassName?: string
	onTransitionEnd: () => void
	isTransitioning: boolean
	onClick?: () => void
}) {
	return (
		<div
			className={clsx(
				'flex h-full w-full',
				isTransitioning ? 'duration-700 transition-transform s-ease' : 'transition-none',
				onClick ? 'cursor-pointer' : ''
			)}
			style={{
				transform: `translateX(${currentImage * -100}%)`,
			}}
			onTransitionEnd={onTransitionEnd}
			onClick={onClick}
		>
			{carousel.map((image: ImageFragment, idx: number) => {
				if (!image.url) {
					return null
				}
				return (
					<div className="relative h-full w-full shrink-0" key={`${image.id}-${idx}`}>
						<Image
							src={image.url}
							width={100}
							height={100}
							alt={image.title ?? 'Carousel image'}
							sizes="80vw"
							className={clsx('object-cover h-full w-full', imageClassName)}
						/>
					</div>
				)
			})}
		</div>
	)
}
